import type { UseFormReturn } from "react-hook-form";

import { SelectorDeComunaBase } from "@/components/informes/SelectorDeComuna";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { Comuna } from "@/lib/comunas";

import type { FacturacionValues } from "./esquemaDeFacturacion";

/** Lo que el formulario necesita saber del maestro de comunas. */
export interface ComunasParaElFormulario {
  data: Comuna[] | undefined;
  isPending: boolean;
  isError: boolean;
}

/**
 * Los ocho campos de la facturación, SIN `<form>` ni botón: quien los usa pone
 * el `<Form>` de react-hook-form alrededor y decide qué hace al enviar.
 */
export function CamposDeFacturacion({
  form,
  comunas,
}: {
  form: UseFormReturn<FacturacionValues>;
  comunas: ComunasParaElFormulario;
}) {
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2">
        <FormField
          control={form.control}
          name="rut"
          render={({ field }) => (
            <FormItem>
              <FormLabel>RUT</FormLabel>
              <FormControl>
                <Input placeholder="76.543.210-K" autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="razon_social"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Razón social</FormLabel>
              <FormControl>
                <Input placeholder="Inmobiliaria del Maule SpA" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="giro"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Giro</FormLabel>
            <FormControl>
              <Input placeholder="Inversiones inmobiliarias" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid gap-5 sm:grid-cols-[1.6fr_1fr]">
        <FormField
          control={form.control}
          name="direccion"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Dirección de facturación</FormLabel>
              <FormControl>
                <Input placeholder="Av. Libertad 1234, oficina 501" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="comuna_codigo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Comuna</FormLabel>
              <FormControl>
                <SelectorDeComunaBase
                  comunas={comunas.data}
                  cargando={comunas.isPending}
                  conError={comunas.isError}
                  valor={field.value || null}
                  onValorChange={(codigo) => field.onChange(codigo ?? "")}
                  invalido={!!form.formState.errors.comuna_codigo}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="email_facturacion"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Correo para la factura</FormLabel>
            <FormControl>
              <Input type="email" placeholder="contabilidad@empresa.cl" {...field} />
            </FormControl>
            <FormDescription>
              Puede ser distinto del correo con el que nos escribes. Acá enviamos el documento
              tributario.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <FormField
          control={form.control}
          name="telefono"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Teléfono (opcional)</FormLabel>
              <FormControl>
                <Input placeholder="+56 9 1234 5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="contacto_nombre"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contacto de facturación (opcional)</FormLabel>
              <FormControl>
                <Input placeholder="Paula Rojas" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
}
